import { FormStorageService } from 'lib/services/FormStorage/service';
import { Maybe } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

import {
  BATCH_LTL_STORAGE_MAX_BYTES,
  BATCH_LTL_STORAGE_SCHEMA_VERSION,
  BATCH_LTL_STORAGE_TTL_MS,
} from '../constants';
import {
  BatchLTLPersistedRun,
  BatchLTLPersistedState,
  BatchLTLRunsByKey,
} from '../types';

// Service and email are in the key: a later email in the thread can carry its own batch, and
// shared browsers can switch accounts.
export function buildBatchLTLStorageKey(
  serviceID: number,
  threadId: string,
  emailId: number
): string {
  return `batchLTL_${serviceID}_${threadId}_${emailId}`;
}

// Saved work for this email, or null when there is none, it expired or its shape is old.
export function loadBatchLTLState(
  key: string,
  emailId: number,
  now: number = Date.now()
): Maybe<BatchLTLPersistedState> {
  const saved = FormStorageService.getFormState<BatchLTLPersistedState, null>(
    key
  );
  const state = saved?.values;
  if (!state) {
    return null;
  }

  const isExpired = now - state.savedAt > BATCH_LTL_STORAGE_TTL_MS;
  if (
    state.schemaVersion !== BATCH_LTL_STORAGE_SCHEMA_VERSION ||
    state.emailId !== emailId ||
    isExpired
  ) {
    FormStorageService.clearFormState(key);
    return null;
  }

  return state;
}

// Skips the save (and reports it) when the entry would crowd out other saved forms.
export function saveBatchLTLState(
  key: string,
  threadId: string,
  state: BatchLTLPersistedState
): void {
  const size = JSON.stringify(state).length;
  if (size > BATCH_LTL_STORAGE_MAX_BYTES) {
    captureException(new Error('Batch LTL saved state is too large'), {
      functionName: 'saveBatchLTLState',
      size,
    });
    return;
  }

  FormStorageService.saveFormState<BatchLTLPersistedState, null>(key, {
    threadID: threadId,
    clickedSuggestion: null,
    values: state,
    dirtyFields: null,
  });
}

export function clearBatchLTLState(key: string): void {
  FormStorageService.clearFormState(key);
}

// Job pointers only: quotes are re-read from the server by job id.
export function toPersistedRuns(
  runs: BatchLTLRunsByKey
): Record<number, BatchLTLPersistedRun> {
  return Object.fromEntries(
    Object.entries(runs).map(([key, run]) => [
      key,
      {
        status: run.status,
        jobId: run.jobId,
        startedAt: run.startedAt,
        stops: run.stops,
        failureMessage: run.failureMessage,
      },
    ])
  );
}

// Saved batch for the first of these emails that has one (the open email and the emails of
// its pending LTL quote requests).
export function findBatchLTLState(
  serviceID: number,
  threadId: string,
  emailIds: number[]
): Maybe<BatchLTLPersistedState> {
  for (const emailId of new Set(emailIds)) {
    const state = loadBatchLTLState(
      buildBatchLTLStorageKey(serviceID, threadId, emailId),
      emailId
    );
    if (state) {
      return state;
    }
  }

  return null;
}
